import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';
import { subscribeStatus } from './realtime.js';

const RENDER_ACTIVE = new Set(['queued', 'rendering']);
const PUBLISH_ACTIVE = new Set(['scheduled', 'publishing']);

export function useClipPipeline(clipId, { onClipRefresh, toast } = {}) {
  const [renderJob, setRenderJob] = useState(null);
  const [publishJob, setPublishJob] = useState(null);

  const refreshRender = useCallback(async () => {
    if (!clipId) return null;
    let data;
    try {
      data = await api.renderStatus(clipId);
    } catch {
      // Clip may be deleted (404) or a transient error — don't spam the console
      // or reject the polling chain; the editor surfaces a deleted clip on load.
      return null;
    }
    if (!data?.status || data.status === 'none') {
      setRenderJob(null);
      return null;
    }
    setRenderJob(data);
    if (data.status === 'done' && data.rendered_ready) {
      onClipRefresh?.();
    }
    return data;
  }, [clipId, onClipRefresh]);

  const refreshPublish = useCallback(async () => {
    if (!clipId) return null;
    try {
      const { items } = await api.publishActiveForClip(clipId);
      const job = items?.[0] || null;
      setPublishJob(job);
      return job;
    } catch {
      return null;
    }
  }, [clipId]);

  const refreshAll = useCallback(async () => {
    const [r, p] = await Promise.all([refreshRender(), refreshPublish()]);
    return { render: r, publish: p };
  }, [refreshRender, refreshPublish]);

  useEffect(() => {
    if (!clipId) return undefined;
    refreshAll();
    return subscribeStatus({
      onRenderJob: (job) => {
        if (job?.clip_id !== clipId) return;
        if (!job?.status || job.status === 'none') {
          setRenderJob(null);
          return;
        }
        setRenderJob(job);
        if (job.status === 'done') {
          onClipRefresh?.();
          toast?.('рендер готов', 'ok');
        }
        if (job.status === 'failed') {
          toast?.(job.error_message || 'рендер не удался', 'err');
        }
      },
      onPublishJob: (job) => {
        if (job?.clip_id !== clipId) return;
        if (PUBLISH_ACTIVE.has(job.status)) {
          setPublishJob(job);
          return;
        }
        if (job.status === 'published') {
          setPublishJob(job);
          onClipRefresh?.();
          toast?.('опубликовано', 'ok');
        } else if (job.status === 'failed') {
          setPublishJob(job);
          toast?.(job.error_message || 'публикация не удалась', 'err');
        } else {
          setPublishJob(null);
        }
      },
    });
  }, [clipId, refreshAll, onClipRefresh, toast]);

  const renderActive = !!(renderJob && RENDER_ACTIVE.has(renderJob.status));
  const publishActive = !!(publishJob && PUBLISH_ACTIVE.has(publishJob.status));
  const pipelineBusy = renderActive || publishActive;

  useEffect(() => {
    if (!pipelineBusy) return undefined;
    const id = setInterval(refreshAll, 2500);
    return () => clearInterval(id);
  }, [pipelineBusy, refreshAll]);

  const startRender = useCallback(async () => {
    const r = await api.renderClip(clipId);
    setRenderJob({
      id: r.job_id,
      status: r.status || 'queued',
      clip_id: clipId,
      rendered_ready: false,
    });
    return r;
  }, [clipId]);

  return {
    renderJob,
    publishJob,
    renderActive,
    publishActive,
    pipelineBusy,
    startRender,
    refreshAll,
    setPublishJob,
  };
}

export function renderStatusLabel(status) {
  if (status === 'queued') return 'в очереди';
  if (status === 'rendering') return 'рендер…';
  if (status === 'done') return 'готов';
  if (status === 'failed') return 'ошибка';
  return status || '—';
}

export function publishStatusLabel(status, { waitingRender } = {}) {
  if (status === 'scheduled' && waitingRender) return 'ждёт рендер';
  if (status === 'scheduled') return 'запланировано';
  if (status === 'publishing') return 'публикую…';
  if (status === 'published') return 'опубликовано';
  if (status === 'failed') return 'ошибка';
  return status || '—';
}
